import { getAccessToken } from '@/shared/lib/tokenStorage';

import type { DocumentSource } from './documentSource';

/**
 * The bytes of a document.
 *
 * Fetched rather than pointed at, because a URL cannot simply be framed: a
 * browser set to "Download PDFs" overrides `Content-Disposition: inline` for
 * anything in an iframe, and the API's own responses carry
 * `content-security-policy: default-src 'none'`, which forbids embedding
 * outright. Reading the bytes and rendering them onto a canvas sidesteps both,
 * which is the same reason Shelcom's viewer does it this way.
 *
 * This requires the host to allow CORS for our origin — there is no way around
 * that from the client, and it is why the documents bucket needs a CORS policy
 * before any of this renders.
 *
 * Not an RTK Query endpoint by design. What comes back is a transfer whose
 * lifetime belongs to a mounted viewer — a parsed PDF owns a worker, and a blob
 * URL pins memory until it is revoked — rather than cached state keyed by an
 * argument (§6).
 */
export const fetchDocumentBytes = async (
  source: DocumentSource,
  signal: AbortSignal,
): Promise<ArrayBuffer> => {
  // Our own API may need the session token; a bucket must be read anonymously.
  // A public bucket answers `access-control-allow-origin: *`, which the browser
  // refuses to honour for a credentialed request — so sending cookies to one
  // fails the read outright, quite apart from the token being something no
  // third party should receive.
  const isOwnOrigin = source.url.startsWith(window.location.origin);
  const accessToken = getAccessToken();

  const response = await fetch(source.url, {
    signal,
    credentials: isOwnOrigin ? 'include' : 'omit',
    headers: isOwnOrigin && accessToken !== null ? { authorization: accessToken } : {},
  });

  if (!response.ok) {
    throw new Error(`Document request failed with ${String(response.status)}`);
  }

  return response.arrayBuffer();
};
